/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { breakpoints } from '@coursera/cds-core';

import CourseActionBox from 'bundles/course-cards/components/course-card/enterprise/CourseActionBox';
import type { PropsForWithModal as PropsForCourseAndPartnerNameWithModal } from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseAndPartnerName from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import CourseCardAutoenrollmentDisclaimer from 'bundles/course-cards/components/course-card/enterprise/CourseCardAutoenrollmentDisclaimer';
import CourseProgressBox from 'bundles/course-cards/components/course-card/enterprise/CourseProgressBox';
import CourseProgressInfo from 'bundles/course-cards/components/course-card/enterprise/CourseProgressInfo';
import CourseWithProgressLinkButton from 'bundles/course-cards/components/course-card/enterprise/CourseWithProgressLinkButton';
import SkillPathwayTakeAssessmentButton from 'bundles/course-cards/components/course-card/enterprise/SkillPathwayTakeAssessmentButton';
import { getEnterpriseCourseHomeLinkLabel } from 'bundles/course-cards/utils/courseUtils';
import { getProgressByLearnerMaterial } from 'bundles/enterprise-legacy-learner-home/utils/courseUtils';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import type OnDemandLearnerMaterialWeeksV1 from 'bundles/naptimejs/resources/onDemandLearnerMaterialWeeks.v1';
import type OnDemandLearnerMaterialsV1 from 'bundles/naptimejs/resources/onDemandLearnerMaterials.v1';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type OnDemandLearnerSessionsV1 from 'bundles/naptimejs/resources/onDemandLearnerSessions.v1';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

const styles = {
  skillPathwayActionBox: css`
    margin-top: 12px;
    align-items: flex-end;

    ${breakpoints.down('xs')} {
      align-items: center;
    }
  `,
  experimentOneLineLayout: (hasBadges: boolean) => css`
    display: flex;
    align-items: flex-start;
    width: 100%;
    flex-wrap: nowrap;
    gap: 32px;
    position: relative;

    & > .col-sm-8,
    & > .col-md-9 {
      flex: 1 1 0%;
      min-width: 0;
    }

    & > .CourseActionBox,
    & > [class*='CourseActionBox'] {
      flex: 0 0 auto;
      align-items: center;
      align-self: ${hasBadges ? 'flex-start' : 'flex-end'};
      ${hasBadges ? 'padding-top: 0 !important;' : ''}
    }

    .CourseProgressInfo {
      min-width: 0;
      flex: 1 1 0%;
      width: 100%;
    }
  `,
  actionControls: (hasBadges: boolean) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--cds-spacing-50, 4px);
    ${hasBadges ? 'margin-top: var(--cds-spacing-350, 28px);' : ''}

    .CardDropdownWidget {
      position: relative;
      top: auto;
      right: auto;
    }
  `,
};

type Props = {
  course: CoursesV1;
  cardDropdownWidget: JSX.Element;
  learnerSession?: OnDemandLearnerSessionsV1;
  learnerMaterials?: OnDemandLearnerMaterialsV1;
  learnerMaterialWeeks?: OnDemandLearnerMaterialWeeksV1;
  isAutoenrolled?: boolean;
  isPartOfS12n?: boolean;
  hasBadges?: boolean;
  isWeekToModuleEnabled?: boolean;
} & Omit<PropsForCourseAndPartnerNameWithModal, 'courseId' | 'courseName' | 'partnerIds'>;

const CourseEnrolledRunningPublicSessionCard = ({
  course: { id, name, homeLink, partnerIds },
  cardDropdownWidget,
  learnerMaterialWeeks,
  learnerMaterials,
  learnerSession,
  isAutoenrolled,
  isPartOfS12n,
  hasBadges = false,
  isWeekToModuleEnabled,
  ...rest
}: Props) => {
  // Add defaults
  const numberOfAssignments = (learnerMaterialWeeks && learnerMaterialWeeks.totalPassablesCount) || 0;
  let isProgressCritical = false;
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const isSkillPathway = badgeMetadata?.isSkillPathway;

  if (learnerMaterials) {
    isProgressCritical = !!learnerMaterials.isProgressCritical;
  }

  const summaryCoreProgressPercentage = getProgressByLearnerMaterial(learnerMaterials);

  // If now it's week 6 and the session is 5 weeks in total, the learnerMaterialWeeks will be undefined
  return (
    <div
      className="rc-CourseEnrolledRunningPublicSessionCard row flex-1"
      css={styles.experimentOneLineLayout(hasBadges)}
    >
      <div className="col-xs-12 col-sm-8 col-md-9 pos-static-override-hack">
        <CourseAndPartnerName
          courseId={id}
          courseName={name}
          partnerIds={partnerIds}
          isPartOfS12n={isPartOfS12n}
          onClick={() => setLocation(homeLink)}
          {...rest}
        />
        <CourseProgressBox>
          <CourseProgressInfo
            summaryCoreProgressPercentage={summaryCoreProgressPercentage}
            isProgressCritical={isProgressCritical}
            numberOfAssignments={numberOfAssignments}
            courseName={name}
          />
        </CourseProgressBox>
        {isAutoenrolled && <CourseCardAutoenrollmentDisclaimer />}
      </div>
      <CourseActionBox>
        <div css={styles.actionControls(hasBadges)}>
          {cardDropdownWidget}
          <div
            className={
              isSkillPathway ? 'horizontal-box align-items-vertical-center' : 'vertical-box align-items-absolute-center'
            }
            css={isSkillPathway ? styles.skillPathwayActionBox : undefined}
          >
            <CourseWithProgressLinkButton
              link={homeLink}
              progress={summaryCoreProgressPercentage}
              trackingNamePrefix="enrolled_running_public"
              courseName={name}
              buttonTextOverride={getEnterpriseCourseHomeLinkLabel(summaryCoreProgressPercentage)}
            />
            {isSkillPathway && <SkillPathwayTakeAssessmentButton courseId={id} />}
          </div>
        </div>
      </CourseActionBox>
    </div>
  );
};

export default CourseEnrolledRunningPublicSessionCard;
